import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrophy, faUserTie } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';

const MEDAL_COLORS = ['#f59e0b', '#9ca3af', '#cd7f32'];
const ROW_COLORS = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#f43f5e', '#06b6d4'];

const SupervisorLeaderboard = ({ supervisors }) => {
    const { t } = useTranslation();
    if (!supervisors?.length) return null;

    const maxWeight = Math.max(...supervisors.map((s) => parseFloat(s.total_weight_tons) || 0), 1);

    return (
        <div style={cardStyle}>
            {/* Header */}
            <div style={headerStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{
                        width: 36, height: 36, borderRadius: 10,
                        backgroundColor: '#fffbeb',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#f59e0b', fontSize: 16,
                    }}>
                        <FontAwesomeIcon icon={faTrophy} />
                    </div>
                    <div>
                        <div style={titleStyle}>{t('supervisorAnalytics.leaderboard.title')}</div>
                        <div style={subtitleStyle}>{t('supervisorAnalytics.leaderboard.subtitle')}</div>
                    </div>
                </div>
                <span style={badgeStyle}>{supervisors.length} {t('supervisorAnalytics.leaderboard.supervisorsCountSuffix')}</span>
            </div>

            {/* Table header */}
            <div style={tableHeaderStyle}>
                <div style={{ width: 40, flexShrink: 0, textAlign: 'center' }}>#</div>
                <div style={{ flex: 2, minWidth: 120 }}>{t('supervisorAnalytics.columns.supervisor')}</div>
                <div style={{ flex: 1, minWidth: 80, textAlign: 'right' }}>{t('supervisorAnalytics.columns.assigned')}</div>
                <div style={{ flex: 1, minWidth: 80, textAlign: 'right' }}>{t('supervisorAnalytics.columns.completed')}</div>
                <div style={{ flex: 2, minWidth: 140 }}>{t('supervisorAnalytics.columns.totalWeight')}</div>
                <div style={{ flex: 1, minWidth: 80, textAlign: 'right' }}>{t('supervisorAnalytics.columns.loadingCompleted')}</div>
                <div style={{ flex: 1, minWidth: 80, textAlign: 'right' }}>{t('supervisorAnalytics.columns.unloadingCompleted')}</div>
            </div>

            {/* Rows */}
            <div style={{ padding: '8px 20px 20px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                {supervisors.map((s, i) => {
                    const color = ROW_COLORS[i % ROW_COLORS.length];
                    const weightPct = ((parseFloat(s.total_weight_tons) || 0) / maxWeight) * 100;
                    const totalAssigned = (s.loading_assigned || 0) + (s.unloading_assigned || 0);

                    return (
                        <div
                            key={s.supervisor_id}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 12,
                                padding: '10px 14px', borderRadius: 10,
                                backgroundColor: i === 0 ? '#fffbeb' : i === 1 ? '#f9fafb' : '#ffffff',
                                border: `1px solid ${i < 3 ? `${MEDAL_COLORS[i]}33` : '#f3f4f6'}`,
                                transition: 'box-shadow 0.15s',
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.06)')}
                            onMouseLeave={(e) => (e.currentTarget.style.boxShadow = 'none')}
                        >
                            {/* Rank */}
                            <div style={{
                                width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                backgroundColor: i < 3 ? `${MEDAL_COLORS[i]}22` : '#f3f4f6',
                                color: i < 3 ? MEDAL_COLORS[i] : '#6b7280',
                                fontSize: 12, fontWeight: 800,
                            }}>
                                {i + 1}
                            </div>

                            {/* Name + avatar */}
                            <div style={{ flex: 2, minWidth: 120, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div style={{
                                    width: 32, height: 32, borderRadius: 10,
                                    backgroundColor: `${color}18`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    color, fontSize: 12, flexShrink: 0,
                                }}>
                                    <FontAwesomeIcon icon={faUserTie} />
                                </div>
                                <div>
                                    <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>
                                        {s.supervisor_name}
                                    </div>
                                    <div style={{ fontSize: 11, color: '#9ca3af' }}>
                                        {s.total_completed} {t('supervisorAnalytics.labels.completedSuffix')}
                                    </div>
                                </div>
                            </div>

                            {/* Assigned */}
                            <div style={{ flex: 1, minWidth: 80, textAlign: 'right', fontSize: 13, fontWeight: 600, color: '#374151' }}>
                                {totalAssigned}
                            </div>

                            {/* Completed */}
                            <div style={{ flex: 1, minWidth: 80, textAlign: 'right', fontSize: 14, fontWeight: 700, color: '#111827' }}>
                                {s.total_completed}
                            </div>

                            {/* Weight bar */}
                            <div style={{ flex: 2, minWidth: 140 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                                    <span style={{ fontSize: 11, color: '#6b7280' }}>{t('common.analyticsColumns.weight')}</span>
                                    <span style={{ fontSize: 12, fontWeight: 700, color }}>
                                        {parseFloat(s.total_weight_tons || 0).toFixed(1)} T
                                    </span>
                                </div>
                                <div style={{ height: 5, borderRadius: 3, backgroundColor: '#e5e7eb' }}>
                                    <div style={{
                                        height: '100%', borderRadius: 3,
                                        backgroundColor: color,
                                        width: `${weightPct}%`,
                                        transition: 'width 0.5s',
                                    }} />
                                </div>
                            </div>

                            {/* Loading completed */}
                            <div style={{ flex: 1, minWidth: 80, textAlign: 'right' }}>
                                <span style={{
                                    fontSize: 12, fontWeight: 600,
                                    color: '#0891b2', backgroundColor: '#ecfeff',
                                    borderRadius: 12, padding: '2px 8px',
                                }}>
                                    {s.loading_completed}/{s.loading_assigned}
                                </span>
                            </div>

                            {/* Unloading completed */}
                            <div style={{ flex: 1, minWidth: 80, textAlign: 'right' }}>
                                <span style={{
                                    fontSize: 12, fontWeight: 600,
                                    color: '#7c3aed', backgroundColor: '#f5f3ff',
                                    borderRadius: 12, padding: '2px 8px',
                                }}>
                                    {s.unloading_completed}/{s.unloading_assigned}
                                </span>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const cardStyle = {
    backgroundColor: '#fff', borderRadius: 16,
    border: '1px solid #f3f4f6',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    overflow: 'hidden',
};
const headerStyle = {
    padding: '18px 20px 14px',
    borderBottom: '1px solid #f3f4f6',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
};
const titleStyle = { fontSize: 15, fontWeight: 700, color: '#111827' };
const subtitleStyle = { fontSize: 12, color: '#9ca3af', marginTop: 2 };
const badgeStyle = {
    fontSize: 11, fontWeight: 700, color: '#f59e0b',
    backgroundColor: '#fffbeb', borderRadius: 20, padding: '3px 10px',
};
const tableHeaderStyle = {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '10px 34px',
    backgroundColor: '#f9fafb',
    fontSize: 11, fontWeight: 700, color: '#9ca3af',
    textTransform: 'uppercase', letterSpacing: '0.4px',
    borderBottom: '1px solid #f3f4f6',
};

export default SupervisorLeaderboard;
